import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Schedule, Course, Instructor } from '../../types';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';
import {
  compareSessionDateAsc,
  formatDate,
  isUpcomingSession,
  startOfToday,
} from '../../utils/dateUtils';

const ManageSchedules: React.FC = () => {
  usePageTitle('Manage Schedules');
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [creatingWebinar, setCreatingWebinar] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);
  const authFetch = useAuthFetch();

  const [formData, setFormData] = useState({
    courseId: '',
    sessionDate: '',
    startTime: '09:00',
    endTime: '17:00',
    timezone: 'PT',
    instructorId: '',
    maxSeats: 20,
    status: 'scheduled' as Schedule['status'],
  });

  const formatTimeForSave = (time24: string, zone: string): string => {
    const [hStr, mStr] = time24.split(':');
    const h = parseInt(hStr, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${mStr} ${ampm} ${zone}`;
  };

  const parseStoredTime = (stored: string): { time24: string; zone: string } => {
    const m12 = stored.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)\s*([A-Z]{2,3})?$/i);
    if (m12) {
      let h = parseInt(m12[1], 10);
      const min = m12[2];
      const ampm = m12[3].toUpperCase();
      if (ampm === 'PM' && h !== 12) h += 12;
      if (ampm === 'AM' && h === 12) h = 0;
      return {
        time24: `${String(h).padStart(2, '0')}:${min}`,
        zone: (m12[4] || 'PT').toUpperCase(),
      };
    }
    const m24 = stored.match(/^(\d{1,2}):(\d{2})$/);
    if (m24) {
      return { time24: `${m24[1].padStart(2, '0')}:${m24[2]}`, zone: 'PT' };
    }
    return { time24: '09:00', zone: 'PT' };
  };

  const fetchData = useCallback(async () => {
    try {
      const [schedulesRes, coursesRes, instructorsRes] = await Promise.all([
        authFetch('/api/schedules'),
        authFetch('/api/courses'),
        authFetch('/api/instructors'),
      ]);
      if (schedulesRes.ok) setSchedules(await schedulesRes.json());
      if (coursesRes.ok) setCourses(await coursesRes.json());
      if (instructorsRes.ok) setInstructors(await instructorsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  // The cutoff is clock-dependent, so hold it in state and roll it forward at
  // local midnight — a page left open overnight would otherwise keep showing
  // yesterday's sessions until the next fetch.
  const [todayStart, setTodayStart] = useState(startOfToday);

  useEffect(() => {
    const nextMidnight = new Date(todayStart);
    nextMidnight.setDate(nextMidnight.getDate() + 1);
    const delay = Math.max(nextMidnight.getTime() - Date.now(), 0) + 1000;
    const timer = setTimeout(() => setTodayStart(startOfToday()), delay);
    return () => clearTimeout(timer);
  }, [todayStart]);

  // Default view is upcoming only (session date today or later). Past sessions
  // stay reachable behind the toggle so they can still be edited or deleted.
  // Status is deliberately NOT filtered here: a cancelled or completed future
  // session must remain visible to an admin.
  const pastCount = useMemo(
    () => schedules.filter((s) => !isUpcomingSession(s.sessionDate, todayStart)).length,
    [schedules, todayStart]
  );

  const visibleSchedules = useMemo(
    () =>
      schedules
        .filter((s) => showPast || isUpcomingSession(s.sessionDate, todayStart))
        .sort(compareSessionDateAsc),
    [schedules, showPast, todayStart]
  );

  const courseMap = courses.reduce<Record<string, Course>>((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});

  const instructorMap = instructors.reduce<Record<string, Instructor>>((acc, i) => {
    acc[i.id] = i;
    return acc;
  }, {});

  const handleEdit = (schedule: Schedule) => {
    setEditing(schedule);
    const parsedStart = parseStoredTime(schedule.startTime);
    const parsedEnd = parseStoredTime(schedule.endTime);
    setFormData({
      courseId: schedule.courseId,
      sessionDate: schedule.sessionDate,
      startTime: parsedStart.time24,
      endTime: parsedEnd.time24,
      timezone: parsedStart.zone,
      instructorId: schedule.instructorId,
      maxSeats: schedule.maxSeats,
      status: schedule.status,
    });
    setShowForm(true);
    setError('');
  };

  const handleCreate = () => {
    setEditing(null);
    setFormData({
      courseId: '',
      sessionDate: '',
      startTime: '09:00',
      endTime: '17:00',
      timezone: 'PT',
      instructorId: '',
      maxSeats: 20,
      status: 'scheduled',
    });
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!HHMM.test(formData.startTime) || !HHMM.test(formData.endTime)) {
      setError('Start Time and End Time are required and must be valid HH:MM values.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const url = editing ? `/api/schedules/${editing.id}` : '/api/schedules';
      const method = editing ? 'PUT' : 'POST';
      const { timezone, startTime, endTime, ...rest } = formData;
      const payload = {
        ...rest,
        startTime: formatTimeForSave(startTime, timezone),
        endTime: formatTimeForSave(endTime, timezone),
      };
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        await fetchData();
        setShowForm(false);
        setEditing(null);
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to save schedule');
      }
    } catch {
      setError('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    try {
      const res = await authFetch(`/api/schedules/${id}`, { method: 'DELETE' });
      if (res.ok) fetchData();
    } catch (err) {
      console.error('Error deleting schedule:', err);
    }
  };

  const handleCreateWebinar = async (scheduleId: string) => {
    setCreatingWebinar(scheduleId);
    try {
      const res = await authFetch(`/api/schedules/${scheduleId}/create-webinar`, {
        method: 'POST',
      });
      if (res.ok) {
        await fetchData();
      } else {
        const data = await res.json();
        alert(data.message || 'Failed to create Teams webinar');
      }
    } catch {
      alert('Error creating Teams webinar');
    } finally {
      setCreatingWebinar(null);
    }
  };

  const parseTeamsWebinarEventId = (url: string): string | null => {
    try {
      const parsed = new URL(url.trim());
      if (parsed.hostname !== 'events.teams.microsoft.com') return null;
      const match = parsed.pathname.match(/^\/event\/([0-9a-fA-F-]+@[0-9a-fA-F-]+)/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  };

  const handleUseExistingWebinar = async (scheduleId: string) => {
    const url = window.prompt('Enter the Teams webinar event page URL:');
    if (!url) return;

    const eventId = parseTeamsWebinarEventId(url);
    if (!eventId) {
      alert('Invalid Teams webinar URL. Expected format:\nhttps://events.teams.microsoft.com/event/{eventId}@{tenantId}');
      return;
    }

    setCreatingWebinar(scheduleId);
    try {
      const res = await authFetch(`/api/schedules/${scheduleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamsWebinarEventId: eventId,
          webinarJoinUrl: url.trim(),
          webinarStatus: 'published',
        }),
      });
      if (res.ok) {
        await fetchData();
      } else {
        const data = await res.json();
        alert(data.message || 'Failed to save webinar link');
      }
    } catch {
      alert('Error saving webinar link');
    } finally {
      setCreatingWebinar(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading schedules...</p>
      </div>
    );
  }

  return (
    <div className="admin-manage-page">
      <section className="dashboard-header">
        <div className="section-container">
          <div className="section-header-row">
            <h1 className="page-title">Manage Schedules</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={showPast}
                  onChange={(e) => setShowPast(e.target.checked)}
                />
                Show past sessions{pastCount > 0 ? ` (${pastCount})` : ''}
              </label>
              <button className="btn btn-primary" onClick={handleCreate}>+ Create Schedule</button>
            </div>
          </div>
        </div>
      </section>

      {showForm && (
        <section className="section">
          <div className="section-container">
            <div className="admin-form card">
              <h2>{editing ? 'Edit Schedule' : 'Create Schedule'}</h2>
              {error && <div className="form-error">{error}</div>}
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Course *</label>
                  <select
                    className="form-input"
                    value={formData.courseId}
                    onChange={(e) => setFormData((p) => ({ ...p, courseId: e.target.value }))}
                  >
                    <option value="">Select course</option>
                    {courses.filter((c) => c.active).map((c) => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Instructor *</label>
                  <select
                    className="form-input"
                    value={formData.instructorId}
                    onChange={(e) => setFormData((p) => ({ ...p, instructorId: e.target.value }))}
                  >
                    <option value="">Select instructor</option>
                    {instructors.map((i) => (
                      <option key={i.id} value={i.id}>{i.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Session Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.sessionDate}
                    onChange={(e) => setFormData((p) => ({ ...p, sessionDate: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Max Seats</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxSeats}
                    onChange={(e) => setFormData((p) => ({ ...p, maxSeats: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Start Time</label>
                  <input
                    type="time"
                    className="form-input"
                    value={formData.startTime}
                    onChange={(e) => setFormData((p) => ({ ...p, startTime: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    className="form-input"
                    value={formData.endTime}
                    onChange={(e) => setFormData((p) => ({ ...p, endTime: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Time Zone</label>
                  <select
                    className="form-input"
                    value={formData.timezone}
                    onChange={(e) => setFormData((p) => ({ ...p, timezone: e.target.value }))}
                  >
                    <option value="PT">Pacific (PT)</option>
                    <option value="MT">Mountain (MT)</option>
                    <option value="CT">Central (CT)</option>
                    <option value="ET">Eastern (ET)</option>
                  </select>
                </div>
                {editing && (
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      className="form-input"
                      value={formData.status}
                      onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value as Schedule['status'] }))}
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button className="btn btn-outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="section-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Course</th>
                <th>Instructor</th>
                <th>Date</th>
                <th>Time</th>
                <th>Enrollment</th>
                <th>Teams Webinar</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleSchedules.map((schedule) => (
                <tr key={schedule.id}>
                  <td>{courseMap[schedule.courseId]?.title || schedule.courseId}</td>
                  <td>{instructorMap[schedule.instructorId]?.name || schedule.instructorId}</td>
                  <td>{formatDate(schedule.sessionDate)}</td>
                  <td>{schedule.startTime} - {schedule.endTime}</td>
                  <td>{schedule.currentEnrollment} / {schedule.maxSeats}</td>
                  <td>
                    {schedule.teamsWebinarEventId ? (
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => window.open(schedule.webinarJoinUrl || `https://events.teams.microsoft.com/event/${schedule.teamsWebinarEventId}`, '_blank')}
                      >
                        View Teams Webinar
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleCreateWebinar(schedule.id)}
                          disabled={creatingWebinar === schedule.id || schedule.status === 'cancelled'}
                        >
                          {creatingWebinar === schedule.id ? 'Creating...' : 'Create Teams Webinar'}
                        </button>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleUseExistingWebinar(schedule.id)}
                          disabled={creatingWebinar === schedule.id || schedule.status === 'cancelled'}
                        >
                          Use Existing
                        </button>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${schedule.status}`}>{schedule.status}</span>
                  </td>
                  <td className="td-actions">
                    <button className="btn btn-text" onClick={() => handleEdit(schedule)}>Edit</button>
                    <button className="btn btn-text btn-danger" onClick={() => handleDelete(schedule.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visibleSchedules.length === 0 && (
            <p className="empty-text">
              {schedules.length === 0
                ? 'No schedules found.'
                : 'No upcoming schedules. Check "Show past sessions" to see earlier ones.'}
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default ManageSchedules;
